/* eslint-disable react-hooks/exhaustive-deps */
import { FC, memo, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, MenuItem, Pagination, Paper, Select, TableSortLabel, Typography, useTheme } from '@mui/material'

import Oops from 'components/atoms/Oops'
import SkeletonLoader from 'components/atoms/loaders/SkeletonLoader'

import { ELEMENT_RADIUS_MID, PRIMARY_GREEN, THEME_GRAY } from 'utils/customizedTheme'
import { getValueFromKeys, removeEmpty } from 'utils/helpers'

import { IColumn } from 'types'

import './MainTable.scss'

const generateColumnWidth = (columns: IColumn[]): string => {
  const columnWidths = columns.map((c) =>
    c?.width ? `minmax(${c?.minWidth ? c.minWidth : 'auto'}, ${c.width})` : 'auto',
  )
  const columnsLayout = columnWidths.join(' ')
  return columnsLayout
}

const tableStyles = (column: IColumn) => {
  return {
    inlineSize: column?.width,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    flex: '1 1 auto',
    justifyContent: column?.align ? `flex-${column.align}` : 'flex-start',
  }
}

interface IProps {
  useTableModel: (tableModelParams: any) => IColumn[]
  filters?: { [key: string]: string | number | any[] }
  getTableData: any
  viewOnly?: boolean
  header?: any
  tableRowParams?: any
  tableModelParams?: any
  stickyFirstColumn?: boolean
}

const MainTable: FC<IProps> = ({ 
  useTableModel, 
  filters = {}, 
  getTableData, 
  viewOnly = false, 
  header, 
  tableRowParams = {}, 
  tableModelParams = {},
  stickyFirstColumn = false
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const columns = useTableModel(tableModelParams)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [ordering, setOrdering] = useState('-id')

  const { isFetching, data, error } = getTableData({
    page,
    page_size: pageSize,
    ordering,
    ...removeEmpty(filters),
  })

  const rows = (data?.results || []).map(el => { return {...el, ...tableRowParams} })
  const allRows = data?.count || 0
    
  // Sort by
  const handleOrderBy = (field: string) => {
    setOrdering(ordering.includes('-') ? field : `-${field}`)
    setPage(1)
  }

  useEffect(() => {
    if (Object.keys(filters)?.length > 0) {
      setPage(1)
    }
  }, [filters])

  return (
    <Paper sx={{ margin: '1rem', background: 'none' }} elevation={0}>
      {header}
      
      {allRows > 0 && <>
        <Box className="thick-border">
          {error ? (
            <Oops />
          ) : (
            <>
              <div className={`table ${stickyFirstColumn ? 'sticky-first-column' : ''}`}>
                <div className='table-head'>
                  <div className='table-row' style={{ gridTemplateColumns: generateColumnWidth(columns) }}>
                    {columns.map((column: any, i: number) => (
                      <Box
                        className={`table-cell ${column?.cellClass ?? ''}`}
                        key={i}
                        onClick={() => handleOrderBy(column.fieldAlias ? column.fieldAlias : column.field)}
                        sx={{
                          '&:hover': column.order ? { cursor: 'pointer' } : 'inherit',
                          background: theme.palette.mode === 'dark' ? '#1e1e1e' : '#fff',
                          minWidth: column.minWidth ? column.minWidth : 'auto',
                          fontWeight: '700',
                          color: PRIMARY_GREEN,
                          ...(tableStyles(column) as { [key: string]: string }),
                        }}
                      >
                        <>
                          <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
                            {column.title}
                            {column.order && (
                              <TableSortLabel
                                active={column.field === ordering.replace('-', '')}
                                direction={ordering.includes('-') ? 'desc' : 'asc'}
                              />
                            )}
                          </Box>
                        </>
                      </Box>
                    ))}
                  </div>
                </div>
                <div className='table-body'>
                  {isFetching ? (
                    <Loading pageSize={pageSize} />
                  ) : (
                    <>
                      {rows &&
                        rows?.length > 0 &&
                        rows.map((row, i) => (
                          <MemoizedRow
                            key={i}
                            row={row}
                            index={i}
                            ordering={ordering}
                            style={{}}
                            useTableModel={useTableModel}
                            handleOrderBy={handleOrderBy}
                            tableModelParams={tableModelParams}
                          />
                        ))}
                    </>
                  )}
                </div>
                {error && <InfoText text={`◽ ${t('loading-table-data-error')} ◽`} />}
                {rows?.length === 0 && !isFetching && !error && (
                  <InfoText text={`◽ ${t('errors.no-items-found')} ◽`} />
                )}
              </div>
            </>
          )}
        </Box>

        <Box className="transparent-background" sx={{ margin: '1rem', display: 'flex', justifyContent: 'space-between' }}>
          {<RowsPerPage pageSize={pageSize} setPageSize={setPageSize} />}
          {pageSize < allRows && (
            <Pagination
              shape='rounded'
              count={Math.ceil(allRows / pageSize)}
              color='primary'
              size='large'
              sx={{ '& .Mui-selected': { color: '#000 !important' } }}
              onChange={(e, pageNumber) => setPage(pageNumber)}
            />
          )}
        </Box>

      </>
    }
    {
      !isFetching && allRows === 0 && <div style={{marginTop: '20px'}}>{t('empty.results')}</div>
    }
    </Paper>
  )
}

export default MainTable

/*
 * Helper components
 */
interface IRowsProps {
  row: any
  index: number
  ordering: string
  style: any
  useTableModel: any
  handleOrderBy: any
  tableModelParams: any
}

const MemoizedRow: FC<IRowsProps> = memo(({ row, index, ordering, style, useTableModel, handleOrderBy, tableModelParams }) => {
  const valueFromKeys = useCallback((row: any, keys: string) => getValueFromKeys(row, keys), [])

  const columns = useTableModel(tableModelParams)


  return (
    <div
      tabIndex={-1}
      key={index}
      className='table-row'
      style={{ gridTemplateColumns: generateColumnWidth(columns), ...style }}
    >
      {columns.map((column, i) => {
        const value = valueFromKeys(row, column.field)
        const valueObj = row[column.field]

        let nestedValue = '';
        if(column.nested_field){
          const nestedObj = row[column.nested_field].find(f => f.id == column.nested_field_id)
          if(nestedObj)
            nestedValue = nestedObj[column.nested_field_value]
        }

        return (
          <div
            key={i}
            className={`table-cell ${column?.cellClass ?? ''}`}
            style={{
              ...(tableStyles(column) as { [key: string]: string }),
              ...(column?.styles && column.styles),
              minWidth: column.minWidth ? column.minWidth : 'auto',
            }}
          >
            <span
              className='mobile-headers'
              onClick={() => handleOrderBy(column.fieldAlias ? column.fieldAlias : column.field)}
            >
              {column.title}
              {column.order && (
                <TableSortLabel
                  active={column.field === ordering.replace('-', '')}
                  direction={ordering.includes('-') ? 'desc' : 'asc'}
                />
              )}
            </span>
            {column.nested_field 
              ? nestedValue
              : (column.format ? column.format(value, row, index, valueObj) : value)
            }
          </div>
        )
      })}
    </div>
  )
})

function Loading({ pageSize }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        alignItems: 'center',
      }}
    >
      {Array.from({ length: pageSize }).map((el, i) => (
        <SkeletonLoader key={i} width={'98%'} height={'50px'} sx={{ marginTop: '0.75rem' }} />
      ))}
    </Box>
  )
}

const InfoText: FC<{ text: string }> = ({ text }) => {
  return <div className='info-text'>{text}</div>
}

const RowsPerPage = ({ pageSize, setPageSize }) => {

  const { t } = useTranslation()
  
  return (
    <Box className="transparent-background"
      sx={{
        display: 'flex',
        alignItems: 'center',
        '& .MuiSelect-select': { padding: '0.55rem 2.5rem 0.55rem 1rem !important' },
      }}
    >
      <Typography sx={{ display: 'inline', marginRight: '1rem' }}>{ t('common.rows-per-page') }:</Typography>
      <Select
        value={pageSize}
        onChange={(e) => setPageSize(e.target.value)}
        sx={{ borderRadius: ELEMENT_RADIUS_MID }}
      >
        <MenuItem value={10}>10</MenuItem>
        <MenuItem value={20}>20</MenuItem>
        <MenuItem value={50}>50</MenuItem>
      </Select>
    </Box>
  )
}
